import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import {
    ConsiderationField,
    ConsiderationFieldChange,
    ConsiderationValueChange,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { EM_DASH, formatDateTime } from '../../../utils/flowOfFundsFormat';
import {
    partyLabel,
    toDisplayValue,
    toOriginalDisplayValue,
} from '../../../utils/flow-of-funds/consideration-field-format';
import { SaveConsiderationChangeNote } from '../../../slices/marketplace/flow-of-funds/thunks/save-consideration-change-note.thunk';

const ORIGINAL_LABEL = 'Original';
const ORIGINAL_CAPTION = 'Set at execution of the purchase agreement';
const NOTE_MAX_LENGTH = 2000;

interface ConsiderationHistoryEntryProps {
    field: ConsiderationField;
    entry: ConsiderationValueChange;
    isCurrent: boolean;
    isEditingNote: boolean;
    noteDraft: string;
    isSaving: boolean;
    onNoteDraftChange: (value: string) => void;
    onStartEdit: () => void;
    onCancelEdit: () => void;
    onSaveNote: () => void;
}

const ConsiderationHistoryEntry = ({
    field,
    entry,
    isCurrent,
    isEditingNote,
    noteDraft,
    isSaving,
    onNoteDraftChange,
    onStartEdit,
    onCancelEdit,
    onSaveNote,
}: ConsiderationHistoryEntryProps) => (
    <span className={`ff-history__entry${isCurrent ? ' ff-history__entry--current' : ''}`}>
        <span className="ff-history__entry-head">
            <span className="ff-history__party">
                {partyLabel(entry.changedBy)} · {isCurrent ? 'current' : 'superseded'}
            </span>
            <span
                className={`ff-history__value${isCurrent ? '' : ' ff-history__value--superseded'}`}>
                {toDisplayValue(field, entry.newValue) || EM_DASH}
            </span>
        </span>
        <span className="ff-history__meta">{formatDateTime(entry.changedOn)}</span>
        {entry.note && !isEditingNote && <span className="ff-history__note">{entry.note}</span>}
        {entry.canEditNote && !isEditingNote && (
            <button type="button" className="ff-history__note-action" onClick={onStartEdit}>
                Edit explanation
            </button>
        )}
        {isEditingNote && (
            <span className="ff-history__note-edit">
                <textarea
                    autoFocus
                    className="form-control ff-history__note-input"
                    value={noteDraft}
                    maxLength={NOTE_MAX_LENGTH}
                    disabled={isSaving}
                    onChange={(event) => onNoteDraftChange(event.target.value)}
                />
                <span className="ff-history__note-actions">
                    {entry.note && (
                        <button
                            type="button"
                            className="btn btn-cancel btn-sm"
                            disabled={isSaving}
                            onClick={onCancelEdit}>
                            Cancel
                        </button>
                    )}
                    <ButtonLoader
                        className="btn btn-primary btn-sm"
                        buttonText="Save explanation"
                        loaderButtonText="Saving…"
                        isLoading={isSaving}
                        disabled={isSaving}
                        onClick={onSaveNote}
                    />
                </span>
            </span>
        )}
    </span>
);

export interface ConsiderationHistoryPopoverProps {
    field: ConsiderationField;
    fieldChange: ConsiderationFieldChange;
    engagementId: number;
    onClose: () => void;
    style?: React.CSSProperties;
}

export const ConsiderationHistoryPopover = React.forwardRef<
    HTMLSpanElement,
    ConsiderationHistoryPopoverProps
>(({ field, fieldChange, engagementId, onClose, style }, ref) => {
    const dispatch = useDispatch() as any;
    const [editingChangeId, setEditingChangeId] = useState<number | null>(() => {
        const defaultEditingEntry = fieldChange.history.find(
            (entry) => !entry.note && entry.canEditNote
        );
        return defaultEditingEntry ? defaultEditingEntry.id : null;
    });
    const [noteDraft, setNoteDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const startEdit = (changeId: number, currentNote: string | null) => {
        setEditingChangeId(changeId);
        setNoteDraft(currentNote ?? '');
    };

    const saveNote = async (changeId: number) => {
        setIsSaving(true);
        const action = await dispatch(
            SaveConsiderationChangeNote({ engagementId, changeId, note: noteDraft.trim() || null })
        );
        setIsSaving(false);
        if (SaveConsiderationChangeNote.fulfilled.match(action)) {
            setEditingChangeId(null);
        }
    };

    return (
        <span className="ff-history" ref={ref} style={style}>
            <span className="ff-history__head">
                <span className="ff-history__title">Proposed changes &amp; explanations</span>
                <button type="button" className="ff-history__close" onClick={onClose}>
                    ×
                </button>
            </span>

            {fieldChange.history.map((entry, index) => (
                <ConsiderationHistoryEntry
                    key={entry.id}
                    field={field}
                    entry={entry}
                    isCurrent={index === 0}
                    isEditingNote={editingChangeId === entry.id}
                    noteDraft={noteDraft}
                    isSaving={isSaving}
                    onNoteDraftChange={setNoteDraft}
                    onStartEdit={() => startEdit(entry.id, entry.note)}
                    onCancelEdit={() => setEditingChangeId(null)}
                    onSaveNote={() => saveNote(entry.id)}
                />
            ))}

            <span className="ff-history__original">
                <span className="ff-history__original-head">
                    <span className="ff-history__original-label">{ORIGINAL_LABEL}</span>
                    <span className="ff-history__original-value">
                        {toOriginalDisplayValue(field, fieldChange.originalValue)}
                    </span>
                </span>
                <span className="ff-history__original-caption">{ORIGINAL_CAPTION}</span>
            </span>
        </span>
    );
});

ConsiderationHistoryPopover.displayName = 'ConsiderationHistoryPopover';
